/**
 * FloorSelectorPanel - Floor selection panel with checkboxes and drag-to-reorder
 * Allows users to select multiple floors and reorder them for grid display
 */

import { useState, useRef } from 'react';
import { Floor } from '../../../../map-editor-shared/store';
import { log, LogCategory } from '../../../../map-editor-shared/lib/logger';
import styles from './FloorSelectorPanel.module.css';

export interface FloorSelectorPanelProps {
  floors: Floor[];
  selectedFloorIds: string[];
  onSelectionChange: (floorIds: string[]) => void;
  /** Display order of floors (controls grid position) */
  displayOrder?: string[];
  /** Callback when display order changes via drag-and-drop */
  onOrderChange?: (newOrder: string[]) => void;
  maxSelection?: number;
  disabled?: boolean;
}

export function FloorSelectorPanel({
  floors,
  selectedFloorIds,
  onSelectionChange,
  displayOrder,
  onOrderChange,
  maxSelection = 999, // Allow unlimited by default (controlled by parent)
  disabled = false,
}: FloorSelectorPanelProps) {
  // Drag state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNodeRef = useRef<HTMLLabelElement | null>(null);

  // Sort floors by displayOrder if provided, otherwise by order
  const sortedFloors = displayOrder && displayOrder.length > 0
    ? displayOrder
        .map(id => floors.find(f => f.id === id))
        .filter((f): f is Floor => f !== undefined)
    : [...floors].sort((a, b) => a.order - b.order);

  // Calculate object count for each floor from graphJson cells (excluding links)
  const getObjectCount = (floor: Floor): number => {
    if (!floor.mapData?.graphJson?.cells) return 0;
    return floor.mapData.graphJson.cells.filter((cell: any) => cell.type !== 'standard.Link').length;
  };

  // Check if floor has map data
  const hasMapData = (floor: Floor): boolean => {
    return !!floor.mapData?.graphJson;
  };

  // Get status summary (placeholder - will integrate with real status later)
  const getStatusSummary = (_floor: Floor): 'ok' | 'warning' | 'error' | 'inactive' => {
    // TODO: Integrate with status store for real-time status
    return 'ok';
  };

  // Handle individual floor checkbox toggle
  const handleFloorToggle = (floorId: string) => {
    if (disabled) return;

    const isSelected = selectedFloorIds.includes(floorId);

    if (isSelected) {
      // Remove from selection
      onSelectionChange(selectedFloorIds.filter((id) => id !== floorId));
    } else {
      // Add to selection (with max limit check)
      if (selectedFloorIds.length >= maxSelection) {
        log.warn(LogCategory.FLOOR, `Maximum ${maxSelection} floors can be selected`);
        // TODO: Show toast notification
        return;
      }
      onSelectionChange([...selectedFloorIds, floorId]);
    }
  };

  // Handle "Select All" button
  const handleSelectAll = () => {
    if (disabled) return;
    const allFloorIds = sortedFloors.map((f) => f.id);
    onSelectionChange(allFloorIds.slice(0, maxSelection));
  };

  // Handle "Clear" button
  const handleClear = () => {
    if (disabled) return;
    onSelectionChange([]);
  };

  // Check if a floor can be selected (not at max limit or already selected)
  const canSelectFloor = (floorId: string): boolean => {
    if (selectedFloorIds.includes(floorId)) return true;
    return selectedFloorIds.length < maxSelection;
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent<HTMLLabelElement>, index: number) => {
    if (disabled || !onOrderChange) return;
    setDraggedIndex(index);
    dragNodeRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    // Add dragging class after a short delay to avoid affecting drag image
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.classList.add(styles.dragging);
      }
    }, 0);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) {
      dragNodeRef.current.classList.remove(styles.dragging);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex || !onOrderChange) return;

    // Create new order
    const currentOrder = sortedFloors.map(f => f.id);
    const newOrder = [...currentOrder];
    const [movedItem] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, movedItem);

    onOrderChange(newOrder);
    handleDragEnd();
  };

  return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>층 선택</h3>
        <div className={styles.selectionCount}>
          {selectedFloorIds.length} / {maxSelection}
        </div>
      </div>

      {/* Reorder hint */}
      {onOrderChange && sortedFloors.length > 1 && (
        <div className={styles.reorderHint}>
          ↕ 드래그하여 순서 변경
        </div>
      )}

      {/* Floor List */}
      <div className={styles.floorList}>
        {sortedFloors.map((floor, index) => {
          const isSelected = selectedFloorIds.includes(floor.id);
          const isDisabled = disabled || !canSelectFloor(floor.id);
          const objectCount = getObjectCount(floor);
          const status = getStatusSummary(floor);
          const isDragOver = dragOverIndex === index && draggedIndex !== index;
          const hasData = hasMapData(floor);

          return (
            <label
              key={floor.id}
              className={`${styles.floorItem} ${isSelected ? styles.selected : ''} ${
                isDisabled ? styles.disabled : ''
              } ${isDragOver ? styles.dragOver : ''} ${!hasData ? styles.noData : ''}`}
              draggable={!disabled && !!onOrderChange}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              title={!hasData ? '맵 데이터가 없습니다. CSV/DXF 파일을 업로드해주세요.' : undefined}
            >
              {/* Drag handle */}
              {onOrderChange && (
                <div className={styles.dragHandle} title="드래그하여 순서 변경">
                  ⋮⋮
                </div>
              )}

              {/* Order number */}
              <div className={styles.orderNumber}>
                {index + 1}
              </div>

              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleFloorToggle(floor.id)}
                  disabled={isDisabled}
                />
              </div>

              <div className={styles.floorInfo}>
                <div className={styles.floorName}>
                  {floor.name}
                  <span className={`${styles.statusDot} ${styles[status]}`} />
                  {!hasData && <span className={styles.noDataBadge}>데이터 없음</span>}
                </div>
                <div className={styles.floorMeta}>
                  {hasData ? `${objectCount} objects` : 'CSV/DXF 업로드 필요'}
                </div>
              </div>
            </label>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className={styles.actions}>
        <button
          onClick={handleSelectAll}
          disabled={disabled}
          className={styles.actionButton}
        >
          모두 선택
        </button>
        <button
          onClick={handleClear}
          disabled={disabled || selectedFloorIds.length === 0}
          className={styles.actionButton}
        >
          선택 해제
        </button>
      </div>

      {/* Warning for max selection */}
      {selectedFloorIds.length >= maxSelection && (
        <div className={styles.warning}>
          최대 {maxSelection}개 층까지 선택할 수 있습니다
        </div>
      )}
    </div>
  );
}
